import { DependencyList, useEffect, useMemo, useState } from "react";
import { useQuery, UseQueryOptions } from '@tanstack/react-query';

interface UseGetDataOptions<TData = any> {
  promise?: () => Promise<any> | null | undefined;
  deps?: DependencyList;
  timeout?: number;
  useCache?: boolean;
  queryKey?: readonly unknown[];
  queryOptions?: Omit<UseQueryOptions<TData>, 'queryKey' | 'queryFn'>;
}

const useGetData = <TData = any>({
  promise,
  deps = [],
  timeout = 0,
  useCache = false,
  queryKey = [],
  queryOptions,
}: UseGetDataOptions<TData>) => {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<any>();
  const [error, setError] = useState<any>();

  // If using cache and queryKey is provided, use TanStack Query
  const queryResult = useQuery({
    queryKey: useCache ? queryKey : [],
    queryFn: async () => {
      if (timeout > 0) {
        await new Promise((resolve) => setTimeout(resolve, timeout));
      }
      const response = await promise?.();
      return response?.data?.message;
    },
    enabled: useCache && !!queryKey && !!promise,
    ...queryOptions,
  });

  // Traditional fetch for non-cached requests
  const fetchData = async () => {
    try {
      if (timeout > 0) {
        await new Promise((resolve) => setTimeout(resolve, timeout));
      }
      const response = await promise?.();
      setData(response?.data?.message);
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      setError(err);
    }
  };

  // Use effect for non-cached requests
  useEffect(() => {
    if (!useCache) {
      setIsLoading(true);
      fetchData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps]);

  return useMemo(
    () => ({
      isLoading: useCache ? queryResult.isLoading : isLoading,
      data: useCache ? queryResult.data : data,
      error: useCache ? queryResult.error : error,
      // Additional TanStack Query features when using cache
      ...(useCache && {
        isFetching: queryResult.isFetching,
        refetch: queryResult.refetch,
        isRefetching: queryResult.isRefetching,
      }),
    }),
    [
      useCache,
      queryResult.isLoading,
      queryResult.data,
      queryResult.error,
      queryResult.isFetching,
      queryResult.refetch,
      queryResult.isRefetching,
      isLoading,
      data,
      error,
    ]
  );
};

export default useGetData;
